import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Modal,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import React, { FC, useMemo, useState, useCallback } from 'react'
import { MedChangeLog } from '../../../types/models/MedChangeLog'
import { IppanMedicine } from '../../../types/models/Medicines'
import { DatePicker } from '../../../components/molecules/DatePicker'
import { SearchableSelectBox } from '../../../components/organisms/SearchableSelectBox'
import dayjs, { Dayjs } from 'dayjs'
import {
  useIppanMedicinesQuery,
  useSpecificMedicinesQuery,
} from '../../medicine'
import {
  useCreateMedChangeLogMutation,
  useUpdateMedChangeLogMutation,
  useDeleteMedChangeLogMutation,
} from '../hooks/mutations/useMedChangeLogMutations'
import { useToast } from '../../../contexts/ToastContext'

type Props = {
  /** 編集対象の MedChangeLog。undefined の場合は新規作成モード */
  medChangeLog?: MedChangeLog
  /** モーダルを閉じるコールバック */
  onClose: () => void
}

/**
 * 薬剤変更モーダルの本体コンポーネント
 *
 * key prop によって medChangeLog が変わるたびに state がリセットされる。
 * 使用例: <MedChangeModalBody key={medChangeLog?.id ?? 'new'} {...} />
 */
export const MedChangeModalBody: FC<Props> = ({ medChangeLog, onClose }) => {
  const theme = useTheme()

  // フォーム状態: key prop でリセットされるため、初期値を直接設定
  const [ippanMed, setIppanMed] = useState<string | null>(
    medChangeLog?.med_ippan_name ?? null
  )
  const [changedFromMed, setChangedFromMed] = useState<string | null>(
    medChangeLog?.original_med_name ?? null
  )
  const [changedToMed, setChangedToMed] = useState<string | null>(
    medChangeLog?.updated_med_name ?? null
  )
  const [startDate, setStartDate] = useState<Dayjs | null>(
    medChangeLog?.start_date ? dayjs(medChangeLog.start_date) : null
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(
    medChangeLog?.end_date ? dayjs(medChangeLog.end_date) : null
  )
  const [isScheduled, setIsScheduled] = useState(
    medChangeLog?.is_scheduled ?? false
  )
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const { showToast } = useToast()

  // TanStack Query hooks
  const { data: ippanMedicines = [] } = useIppanMedicinesQuery()

  const selectedIppanMedId = useMemo(() => {
    const found = ippanMedicines.find(
      (med: IppanMedicine) => med.name === ippanMed
    )
    return found?.id || null
  }, [ippanMedicines, ippanMed])

  const { data: specificMedicines = [] } =
    useSpecificMedicinesQuery(selectedIppanMedId)

  // Mutations
  const createMutation = useCreateMedChangeLogMutation()
  const updateMutation = useUpdateMedChangeLogMutation()
  const deleteMutation = useDeleteMedChangeLogMutation()

  const isSubmitting =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending

  // ソート関数
  const sortInJaOrder = useCallback(
    (medicines: { name: string }[]): string[] => {
      return medicines
        .map((item) => item.name)
        .sort((a, b) => a.localeCompare(b, 'ja'))
    },
    []
  )

  const ippanMedNames = useMemo(
    () => sortInJaOrder(ippanMedicines),
    [ippanMedicines, sortInJaOrder]
  )

  const specificMedNames = useMemo(
    () => sortInJaOrder(specificMedicines),
    [specificMedicines, sortInJaOrder]
  )

  // バリデーション
  const isValidated =
    ippanMed && changedFromMed && changedToMed && startDate && endDate

  const isValidDateSpan = useMemo(() => {
    if (!startDate || !endDate) return false
    return (
      startDate.isSame(endDate, 'day') || startDate.isBefore(endDate, 'day')
    )
  }, [startDate, endDate])

  const isValidEndDate = useMemo(() => {
    if (!endDate) return false
    return dayjs().isSame(endDate, 'day') || dayjs().isBefore(endDate, 'day')
  }, [endDate])

  const validationMessage = useMemo(() => {
    if (!isValidated) return '必要情報を記入してください'
    if (!isValidDateSpan)
      return 'アラート終了日は変更通知開始日以降に設定してください'
    if (!isValidEndDate) return 'アラート終了日は今日以降に設定してください'
    return '変更登録'
  }, [isValidated, isValidDateSpan, isValidEndDate])

  // 送信処理
  const handleSubmit = useCallback(async () => {
    if (
      !ippanMed ||
      !changedFromMed ||
      !changedToMed ||
      !startDate ||
      !endDate ||
      !isValidDateSpan ||
      !isValidEndDate
    ) {
      return
    }

    try {
      const params = {
        med_ippan_name: ippanMed,
        original_med_name: changedFromMed,
        updated_med_name: changedToMed,
        start_date: startDate,
        end_date: endDate,
        is_scheduled: isScheduled,
      }

      if (medChangeLog) {
        await updateMutation.mutateAsync({ ...params, id: medChangeLog.id })
      } else {
        await createMutation.mutateAsync(params)
      }

      onClose()
    } catch (e) {
      console.error('Failed to submit med change log:', e)
      showToast('登録に失敗しました。再度お試しください。', 'error', 6000)
    }
  }, [
    ippanMed,
    changedFromMed,
    changedToMed,
    startDate,
    endDate,
    isScheduled,
    isValidDateSpan,
    isValidEndDate,
    medChangeLog,
    createMutation,
    updateMutation,
    onClose,
    showToast,
  ])

  // 削除処理
  const handleDelete = useCallback(async () => {
    if (!medChangeLog) return

    try {
      await deleteMutation.mutateAsync(medChangeLog.id)
      setIsDeleteConfirmOpen(false)
      onClose()
    } catch (e) {
      console.error('Failed to delete med change log:', e)
      showToast('削除に失敗しました。再度お試しください。', 'error', 6000)
    }
  }, [medChangeLog, deleteMutation, onClose, showToast])

  return (
    <Stack
      direction="column"
      spacing={5}
      p={2}
      sx={{ justifyContent: 'center', height: '100%' }}
    >
      <Stack direction="column" spacing={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isScheduled}
              onChange={() => setIsScheduled(!isScheduled)}
              sx={{ height: 24 }}
              size="small"
              color="info"
            />
          }
          label={'期間内に在庫が切れ次第変更する'}
        />
        <Box>
          <SearchableSelectBox
            label="一般名"
            options={ippanMedNames}
            value={ippanMed || ''}
            setValue={setIppanMed}
          />
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box width={{ xs: '100%', sm: '48%' }}>
            <SearchableSelectBox
              label="変更前"
              options={specificMedNames}
              value={changedFromMed || ''}
              setValue={setChangedFromMed}
            />
          </Box>
          <Box width={{ xs: '100%', sm: '48%' }}>
            <SearchableSelectBox
              label="変更後"
              options={specificMedNames}
              value={changedToMed || ''}
              setValue={setChangedToMed}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box width={{ xs: '100%', sm: '48%' }}>
            <DatePicker
              label="変更通知開始日"
              date={startDate}
              onChange={setStartDate}
            />
          </Box>
          <Box width={{ xs: '100%', sm: '48%' }}>
            <DatePicker
              label="アラート終了日"
              date={endDate}
              onChange={setEndDate}
            />
          </Box>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
        {medChangeLog && (
          <Button
            variant="contained"
            onClick={() => setIsDeleteConfirmOpen(true)}
            fullWidth
            sx={{
              color: '#fff',
              bgcolor: theme.palette.secondary.dark,
              '&:hover': {
                bgcolor: theme.palette.secondary.dark,
                opacity: 0.8,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            変更内容削除
          </Button>
        )}
        <Button
          variant="contained"
          disabled={
            !isValidated || !isValidDateSpan || !isValidEndDate || isSubmitting
          }
          onClick={handleSubmit}
          fullWidth
          sx={{
            color: '#fff',
            bgcolor: theme.palette.primary.dark,
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
              opacity: 0.8,
            },
            '&:disabled': {
              bgcolor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
            transition: 'all 0.2s ease-in-out',
            minHeight: '48px',
          }}
        >
          {isSubmitting ? '登録中...' : validationMessage}
        </Button>
      </Stack>

      {/* 削除確認モーダル */}
      <Modal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Stack
          direction="column"
          spacing={3}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 1)',
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
            textAlign: 'center',
            minWidth: { xs: '280px', sm: '400px' },
            maxWidth: '90vw',
            boxShadow: 24,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            薬剤変更内容を削除してよろしいですか？
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              sx={{
                minWidth: '100px',
                backgroundColor: '#616060',
                color: '#fff',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: '#525252',
                  transform: 'scale(1.05)',
                },
              }}
            >
              {deleteMutation.isPending ? '削除中...' : 'はい'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setIsDeleteConfirmOpen(false)}
              sx={{
                minWidth: '100px',
                borderColor: '#1aa9e9',
                color: '#1aa9e9',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(26, 169, 233, 0.04)',
                  borderColor: '#1aa9e9',
                  transform: 'scale(1.05)',
                },
              }}
            >
              いいえ
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </Stack>
  )
}
